import { useMemo, useState } from "react";

import { MapColumnsStep } from "./components/MapColumnsStep";
import { UploadStep } from "./components/UploadStep";
import { autoMatchAll } from "./lib/autoMatch";
import { readCsvHeaders } from "./lib/csvHeaders";
import { FIELDS, MAX_FILE_SIZE_BYTES, REQUIRED_FIELD_KEYS } from "./lib/fields";
import styles from "./ProspectLetterPage.module.css";

import { ROUTES } from "@/config/routes";
import { triggerDownload } from "@/hooks/download";
import { useTopbarBack } from "@/hooks/useTopbarBack";
import { authService } from "@/services/authService";
import { toolsService } from "@/services/toolsService";
import { ApiError } from "@/types/api";

type Status =
  | { kind: "idle" }
  | { kind: "working" }
  | { kind: "error"; message: string }
  | { kind: "success"; message: string };

/**
 * Prospect Letter Generator: upload a CSV, map its columns to letter fields,
 * and download a ZIP of personalised letters + a mailing-labels document.
 * Generation runs entirely on the server; this is the wizard around it.
 */
export function ProspectLetterPage() {
  const [step, setStep] = useState<"upload" | "map">("upload");
  const [file, setFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [labelTemplate, setLabelTemplate] = useState("avery_l7162");
  const [status, setStatus] = useState<Status>({ kind: "idle" });
  useTopbarBack({ to: ROUTES.tools, label: "Back to tools" });

  const { requiredFilled, hasDupes } = useMemo(() => {
    const values = REQUIRED_FIELD_KEYS.map((key) => mapping[key]).filter(Boolean);
    return {
      requiredFilled: REQUIRED_FIELD_KEYS.every((key) => mapping[key]),
      hasDupes: new Set(values).size !== values.length,
    };
  }, [mapping]);

  function selectFile(next: File) {
    setUploadError(null);
    if (!next.name.toLowerCase().endsWith(".csv")) {
      setUploadError("Invalid file type. Please select a CSV file.");
      return;
    }
    if (next.size > MAX_FILE_SIZE_BYTES) {
      setUploadError("File is too large. Maximum allowed size is 5 MB.");
      return;
    }
    setFile(next);
  }

  async function goToMapping() {
    if (!file) return;
    try {
      const parsed = await readCsvHeaders(file);
      if (parsed.length === 0) {
        setUploadError("Could not read headers from this CSV file.");
        return;
      }
      setHeaders(parsed);
      setMapping(autoMatchAll(FIELDS, parsed).mapping);
      setStatus({ kind: "idle" });
      setStep("map");
    } catch {
      setUploadError("Could not read the file. Please try again.");
    }
  }

  async function generate() {
    if (!file || !requiredFilled || hasDupes) return;
    setStatus({ kind: "working" });

    try {
      const csrfToken = await authService.getCsrfToken();
      if (!csrfToken) {
        setStatus({
          kind: "error",
          message: "Your session has expired. Refresh the page and try again.",
        });
        return;
      }

      // Only send fields the user actually mapped.
      const payload = Object.fromEntries(Object.entries(mapping).filter(([, value]) => value));

      const { blob, filename } = await toolsService.generateProspectLetters({
        file,
        mapping: payload,
        labelTemplate,
        csrfToken,
      });
      triggerDownload(blob, filename ?? "prospect_letters.zip");
      setStatus({
        kind: "success",
        message: "Done. Your ZIP with letters and mailing labels is downloading.",
      });
    } catch (err) {
      setStatus({ kind: "error", message: toErrorMessage(err) });
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Prospect Letter Generator</h1>
      </header>
      {step === "upload" ? (
        <UploadStep
          file={file}
          error={uploadError}
          onSelectFile={selectFile}
          onNext={goToMapping}
        />
      ) : (
        <MapColumnsStep
          headers={headers}
          mapping={mapping}
          labelTemplate={labelTemplate}
          hasDupes={hasDupes}
          canGenerate={requiredFilled && !hasDupes && status.kind !== "working"}
          isWorking={status.kind === "working"}
          statusMessage={
            status.kind === "error" || status.kind === "success" ? status.message : null
          }
          statusKind={
            status.kind === "error" ? "error" : status.kind === "success" ? "success" : null
          }
          onBack={() => {
            setStep("upload");
            setStatus({ kind: "idle" });
          }}
          onChangeMapping={(key, value) => setMapping((prev) => ({ ...prev, [key]: value }))}
          onChangeLabelTemplate={setLabelTemplate}
          onGenerate={generate}
        />
      )}
    </div>
  );
}

function toErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    const details = err.details as { missingColumns?: string[] } | undefined;
    if (details?.missingColumns?.length) {
      return `${err.message} Problem fields: ${details.missingColumns.join(", ")}.`;
    }
    if (err.code === "network_error") {
      return "Network error. Check your connection and try again.";
    }
    return err.message;
  }
  return "Something went wrong. Please try again.";
}
